import { filterValues } from "@prismicio/editor-support/Object"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  createElement,
  type CSSProperties,
  forwardRef,
  type ReactNode,
} from "react"

import { theme, type ThemeKeys } from "../../theme"

export type BoxProps = {
  as?: "div" | "nav" | "article" | "header" | "span"
  children?: ReactNode

  position?: "absolute" | "relative" | "sticky" | "fixed"

  top?: number | string
  left?: number | string
  right?: number | string
  bottom?: number | string

  width?: number | string
  minWidth?: number | string
  maxWidth?: number | string
  height?: number | string
  minHeight?: number | string
  maxHeight?: number | string

  flexBasis?: 0
  flexGrow?: 1
  flexShrink?: 0
  flexWrap?: "wrap"

  animation?: Animation
  transition?: Transition

  padding?: Padding

  border?: Border
  borderRadius?: BorderRadius
  borderStyle?: ThemeKeys<"borderStyle">

  backgroundColor?: ThemeKeys<"color">

  opacity?: ThemeKeys<"opacity">
  visibility?: "hidden" | "visible"

  overflowY?: "auto" | "hidden"
  overflow?: "hidden"

  gridColumn?: string
  gridRow?: string

  transform?: CSSProperties["transform"]
} & DisplayProps

type DisplayProps = {
  gap?: ThemeKeys<"space">
  alignItems?: "flex-start" | "center" | "flex-end"
  justifyContent?: "center" | "space-between" | "end"
} & (BoxGridDisplayProps | BoxFlexDisplayProps)

type BoxGridDisplayProps = {
  display: "grid"
  gridTemplateColumns?: string
  gridTemplateRows?: string
  flexDirection?: never
}

export type BoxFlexDisplayProps = {
  display?: "flex" | "inline-flex"
  flexDirection?: "column" | "row"
  gridTemplateColumns?: never
  gridTemplateRows?: never
}

type Animation = {
  name?: "fadeIn"
  duration?: ThemeKeys<"transitionDuration">
}

type Transition = {
  property: "opacity" | "background-color" | "height"
  duration?: ThemeKeys<"transitionDuration">
  delay?: string
}

type Padding =
  | ThemeKeys<"space">
  | {
      block?: ThemeKeys<"space">
      inline?: ThemeKeys<"space">
      top?: ThemeKeys<"space">
      bottom?: ThemeKeys<"space">
      left?: ThemeKeys<"space">
      right?: ThemeKeys<"space">
    }

type BorderRadius =
  | ThemeKeys<"borderRadius">
  | {
      topLeft?: ThemeKeys<"borderRadius">
      topRight?: ThemeKeys<"borderRadius">
      bottomLeft?: ThemeKeys<"borderRadius">
      bottomRight?: ThemeKeys<"borderRadius">
    }

type Border =
  | boolean
  | { top?: boolean; bottom?: boolean; left?: boolean; right?: boolean }

export const Box = forwardRef<HTMLElement, BoxProps>(function Box(props, ref) {
  const {
    as = "div",
    children,
    display = "flex",
    borderStyle = "solid",
    flexDirection,
    backgroundColor,
    width,
    height,
    flexBasis,
    flexGrow,
    flexShrink,
    flexWrap,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    animation,
    transition,
    padding,
    border,
    position,
    top,
    left,
    right,
    bottom,
    borderRadius,
    opacity,
    visibility,
    overflowY,
    overflow,
    gap,
    alignItems,
    justifyContent,
    gridTemplateColumns,
    gridTemplateRows,
    gridColumn,
    gridRow,
    transform,
    ...implicitProps
  } = props

  const staticStyles: CSSProperties = {
    display,
    flexDirection,
    gridTemplateColumns,
    gridTemplateRows,
    transform,
    top,
    left,
    right,
    bottom,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    gridColumn,
    gridRow,
    flexBasis,
    flexGrow,
    flexShrink,
    flexWrap,
    position,
    opacity,
    visibility,
    overflow,
    overflowY,
    gap,
    alignItems,
    justifyContent,
  }

  const borderRadiusStyles =
    borderRadius && typeof borderRadius === "object"
      ? {
          borderTopLeftRadius: borderRadius.topLeft,
          borderTopRightRadius: borderRadius.topRight,
          borderBottomLeftRadius: borderRadius.bottomLeft,
          borderBottomRightRadius: borderRadius.bottomRight,
        }
      : { borderRadius }

  const backgroundColorStyles: CSSProperties = backgroundColor
    ? { backgroundColor: theme.color[backgroundColor] }
    : {}

  const animationStyles: CSSProperties = animation
    ? {
        animationName: "fadeIn",
        animationDuration: `${animation?.duration ?? 300}ms`,
      }
    : {}

  const transitionStyles: CSSProperties = transition
    ? {
        transitionProperty: transition.property,
        transitionDelay: transition.delay,
        transitionDuration: `${transition?.duration ?? 300}ms`,
      }
    : {}

  // React inline styles are broken if we keep explicitly undefined keys
  // as they could be resetting other, more general/shorthand keys.
  // Example: You set overflow="hidden" and overflowY=undefined => After React is done, you will only have overflowX="hidden".
  const style = filterValues(
    {
      ...staticStyles,
      ...getPaddingStyles(padding),
      ...getBorderStyles(border, borderStyle),
      ...borderRadiusStyles,
      ...backgroundColorStyles,
      ...animationStyles,
      ...transitionStyles,
    },
    isDefined,
  )

  return createElement(as, { style, ref, ...implicitProps }, children)
})

function getPaddingStyles(padding?: Padding): CSSProperties {
  if (padding === undefined) return {}

  if (typeof padding === "object")
    return {
      paddingBlock: padding.block,
      paddingInline: padding.inline,
      paddingTop: padding.top,
      paddingBottom: padding.bottom,
      paddingLeft: padding.left,
      paddingRight: padding.right,
    }

  return { padding }
}

function getBorderStyles(
  border?: Border,
  borderStyle?: ThemeKeys<"borderStyle">,
): CSSProperties {
  if (border === undefined) return {}

  const borderStyles =
    typeof border === "boolean"
      ? { borderStyle }
      : {
          borderTopStyle: border.top ? borderStyle : undefined,
          borderBottomStyle: border.bottom ? borderStyle : undefined,
          borderLeftStyle: border.left ? borderStyle : undefined,
          borderRightStyle: border.right ? borderStyle : undefined,
        }

  const borderColor = theme.color["grey7"]

  const borderColors =
    typeof border === "boolean"
      ? { borderColor }
      : {
          borderTopColor: border.top ? borderColor : undefined,
          borderBottomColor: border.bottom ? borderColor : undefined,
          borderLeftColor: border.left ? borderColor : undefined,
          borderRightColor: border.right ? borderColor : undefined,
        }

  return {
    borderWidth: 1,
    ...borderColors,
    ...borderStyles,
  }
}
